/**
 * An episode as a row, and its right-click menu - shared by the Inbox (what
 * is open) and History (what is over), so the two rooms read the same way.
 *
 * Open rows breathe with the alert glow from `/ui` in their severity's
 * colour; an acknowledged one (stored as the mark `seen`) keeps its colour
 * and stops breathing; past rows are plain, with their verdict in colour. Divs, not a table: the glow's edge is an inset shadow,
 * which a table row does not reliably draw.
 */

import { html, nothing, type TemplateResult } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import type { ArgusInboxItem } from '../../api/types.ts';

/** How long, in the words a person uses: "40 min", "5 h 10 min", "2 days 3 h". */
export function spanWords(fromIso: string, toIso: string | null): string {
  if (!toIso) return '-';
  const minutes = Math.max(0, Math.round((new Date(toIso).getTime() - new Date(fromIso).getTime()) / 60000));
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const rest = minutes % 60;
    return rest ? `${hours} h ${rest} min` : `${hours} h`;
  }
  const days = Math.floor(hours / 24);
  const rest = hours % 24;
  return `${days} day${days === 1 ? '' : 's'}${rest ? ` ${rest} h` : ''}`;
}

export function when(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** A verdict's name, the way the page and History say it. */
export const VERDICT_WORDS: Record<string, string> = {
  confirmed: 'Confirmed',
  not_an_issue: 'Not an issue',
  needs_info: 'Needs more info',
  duplicate: 'Duplicate',
};

/** "Not an issue · sensor fault", from the reason key when no list is to hand. */
export function verdictWords(item: Pick<ArgusInboxItem, 'verdict' | 'verdict_reason'>): string {
  if (!item.verdict) return '';
  const reason = item.verdict_reason && item.verdict !== 'duplicate' ? item.verdict_reason.replace(/_/g, ' ') : '';
  return `${VERDICT_WORDS[item.verdict] ?? item.verdict}${reason ? ` · ${reason}` : ''}`;
}

/** What History says about an episode that is no longer open. */
export function historyWords(item: ArgusInboxItem): string {
  if (item.state === 'judged') {
    return `${verdictWords(item)}${item.judged_by_name ? ` · ${item.judged_by_name}` : ''}${item.ended_at ? '' : ' · still going'}`;
  }
  if (item.state === 'snoozed') return 'muted here - its rule is quiet on this ship for now';
  if (item.state === 'dismissed') {
    return `dismissed${item.marked_by_name ? ` by ${item.marked_by_name}` : ''}${item.ended_at ? '' : ' · still going'}`;
  }
  if (item.state === 'unwatched') return 'no longer watched - its rule was stopped or changed';
  if (item.mark === 'seen') return `ended · acknowledged by ${item.marked_by_name ?? 'someone'}`;
  return 'ended';
}

const SEVERITIES = new Set(['critical', 'watch', 'info']);

export function severityOf(item: ArgusInboxItem): string {
  return SEVERITIES.has(item.severity) ? item.severity : 'watch';
}

export type OpenMenu = (item: ArgusInboxItem, x: number, y: number) => void;

/** Who did something, and when, as the Rules table's two lines. */
function byWhen(name: string | null, at: string | null): TemplateResult {
  return html`<div class="argus-ltext">${name ?? 'Someone'}</div>
    ${at ? html`<div class="argus-lfaint argus-ltext">${when(at)}</div>` : nothing}`;
}

/** What became of an episode that is over: its verdict, or what happened to it. */
function outcome(item: ArgusInboxItem): TemplateResult {
  if (item.verdict) {
    const reason = item.verdict_reason && item.verdict !== 'duplicate' ? item.verdict_reason.replace(/_/g, ' ') : '';
    return html`<span class="argus-verdict-chip is-v-${item.verdict}">${VERDICT_WORDS[item.verdict] ?? item.verdict}</span>
      ${reason ? html`<div class="argus-lfaint argus-ltext">${reason}</div>` : nothing}`;
  }
  const words: Record<string, string> = {
    closed: 'Ended',
    dismissed: 'Dismissed',
    snoozed: 'Muted',
    unwatched: 'No longer watched',
  };
  return html`<span class="argus-verdict-chip is-plain">${words[item.state] ?? item.state}</span>`;
}

export interface RowOptions {
  /** Open the episode's page. */
  onOpen?: (item: ArgusInboxItem) => void;
  /** Acknowledge in one click. Absent when this person may not judge. */
  onAcknowledge?: ((item: ArgusInboxItem) => void) | undefined;
}

/**
 * Episodes as the Rules room's table - the same `argus-ltable`, the same
 * severity edge on the left, the same two-line cells - so the three lists of
 * Argus read as one. `open` picks the Inbox's columns or History's.
 */
export function renderEpisodeTable(
  items: readonly ArgusInboxItem[],
  open: boolean,
  onMenu: OpenMenu,
  opts: RowOptions = {},
  label = open ? 'Open' : 'History',
): TemplateResult {
  return html`<div class="argus-ltable-wrap">
    <table class="argus-ltable argus-etable" aria-label=${label}>
      <colgroup>
        <col />
        <col class="argus-ecol-ship" />
        <col class="argus-ecol-when" />
        <col class="argus-ecol-long" />
        <col class="argus-ecol-status" />
        <col class="argus-ecol-by" />
        <col class="argus-ecol-more" />
      </colgroup>
      <thead>
        <tr>
          <th>Rule</th>
          <th>Ship</th>
          <th>${open ? 'Since' : 'Started'}</th>
          <th>${open ? 'So far' : 'Lasted'}</th>
          <th>${open ? 'Severity' : 'Outcome'}</th>
          <th>${open ? 'Acknowledged' : 'By'}</th>
          <th><span class="argus-visually-hidden">Actions</span></th>
        </tr>
      </thead>
      <tbody>
        ${repeat(items, (i) => i.id, (i) => renderEpisodeRow(i, open, onMenu, opts))}
      </tbody>
    </table>
  </div>`;
}

function renderEpisodeRow(
  item: ArgusInboxItem,
  open: boolean,
  onMenu: OpenMenu,
  opts: RowOptions,
): TemplateResult {
  const severity = severityOf(item);
  const seen = item.mark === 'seen';
  const onOpen = opts.onOpen;
  // Still going (open, dismissed, or left by a stopped rule): measured to its
  // last reading, never to the wall clock.
  const until = item.ended_at ?? item.last_at;
  const ack = opts.onAcknowledge;
  const byCell = open
    ? seen
      ? html`<div class="argus-ack-done">✓ ${byWhen(item.marked_by_name, item.marked_at)}</div>`
      : ack
        ? html`<button
            type="button"
            class="argus-ack-btn is-${severity}"
            title="Say you have seen it: the glow stops, and it stays open until the watcher closes it"
            @click=${(e: MouseEvent) => {
              e.stopPropagation();
              ack(item);
            }}
          >
            Acknowledge
          </button>`
        : html`<span class="argus-lfaint">not yet</span>`
    : item.verdict
      ? byWhen(item.judged_by_name, item.judged_at)
      : item.mark
        ? byWhen(item.marked_by_name, item.marked_at)
        : html`<span class="argus-lfaint">-</span>`;
  // Open and not yet acknowledged: the dot breathes. Acknowledged: it is still.
  const dot = open
    ? html`<span class="glow-dot ${seen ? '' : 'glow-alive'} is-${severity}" aria-hidden="true"></span>`
    : html`<span class="argus-ibx-past-dot ${item.verdict ? `is-v-${item.verdict}` : ''}" aria-hidden="true"></span>`;
  return html`<tr
    class="argus-lrow argus-erow is-sev-${severity} ${open ? 'is-open' : 'is-past'} ${seen ? 'is-seen' : ''}"
    tabindex="0"
    aria-label=${`${item.rule_name} on ${item.entity_name}`}
    @click=${(e: MouseEvent) => {
      // The ⋯ and Acknowledge buttons are the row's actions, not a way into it.
      if (!onOpen || (e.target as Element).closest('.argus-ibx-more, .argus-ack-btn')) return;
      onOpen(item);
    }}
    @keydown=${(e: KeyboardEvent) => {
      if (onOpen && e.key === 'Enter' && e.target === e.currentTarget) onOpen(item);
    }}
    @contextmenu=${(e: MouseEvent) => {
      e.preventDefault();
      onMenu(item, e.clientX, e.clientY);
    }}
  >
    <td>
      <div class="argus-lname">
        ${dot}<span class="argus-ltext">${item.rule_name}</span>
        ${open && item.verdict === 'needs_info' ? html`<span class="argus-chip is-info">needs info</span>` : nothing}
        ${open && item.owner_name ? html`<span class="argus-ibx-seen">${item.owner_name} assigned</span>` : nothing}
      </div>
      <div class="argus-lsentence argus-ltext" title=${item.verdict_note ?? item.sentence ?? ''}>
        ${!open && item.verdict_note ? html`“${item.verdict_note}”` : item.sentence}
      </div>
    </td>
    <td><div class="argus-ltext" title=${item.entity}>${item.entity_name}</div></td>
    <td><div class="argus-ltext">${when(item.started_at)}</div></td>
    <td>
      <div class="argus-ltext" title=${open ? 'From its first reading to the latest one where it held' : 'How long it lasted'}>
        ${spanWords(item.started_at, until)}
      </div>
      ${!open && !item.ended_at ? html`<div class="argus-lfaint">still going</div>` : nothing}
    </td>
    <td>${open ? html`<span class="argus-chip is-${severity}">${severity}</span>` : outcome(item)}</td>
    <td>${byCell}</td>
    <td class="argus-emore">
      <button
        type="button"
        class="argus-ibx-more"
        aria-label=${`Actions for ${item.rule_name} on ${item.entity_name}`}
        title="Acknowledge, open the rule"
        @click=${(e: MouseEvent) => {
          e.stopPropagation();
          const box = (e.currentTarget as HTMLElement).getBoundingClientRect();
          onMenu(item, box.right - 190, box.bottom + 4);
        }}
      >
        ⋯
      </button>
    </td>
  </tr>`;
}

export interface MenuActions {
  /** Null takes the mark off. Absent when this person may not judge. */
  mark?: ((item: ArgusInboxItem, mark: 'seen' | 'dismissed' | null) => void) | undefined;
  openRule: (item: ArgusInboxItem) => void;
  /** Filter the room to this rule, or back to every rule. */
  filter: (item: ArgusInboxItem | null) => void;
  filtered: boolean;
}

/** The right-click menu at a point, kept on screen. */
export function renderEpisodeMenu(
  at: { item: ArgusInboxItem; x: number; y: number } | null,
  actions: MenuActions,
): TemplateResult | typeof nothing {
  if (!at) return nothing;
  const { item } = at;
  const x = Math.min(at.x, window.innerWidth - 208);
  const y = Math.min(at.y, window.innerHeight - 208);
  const entry = (icon: string, label: string, run: () => void): TemplateResult =>
    html`<button class="cat-menu-item" role="menuitem" type="button" @click=${run}>
      <span class="cat-menu-icon" aria-hidden="true">${icon}</span> ${label}
    </button>`;
  const mark = actions.mark;
  const judging = mark
    ? [
        item.state === 'open'
          ? item.mark === 'seen'
            ? entry('○', 'Undo acknowledge', () => mark(item, null))
            : entry('✓', 'Acknowledge', () => mark(item, 'seen'))
          : nothing,
        // Dismiss gave way to verdicts (T2), given on the episode's page; a
        // Dismiss made before then can still be taken back.
        item.mark === 'dismissed' ? entry('↺', 'Undo dismiss', () => mark(item, null)) : nothing,
      ]
    : [];
  const judged = mark && (item.state === 'open' || item.mark === 'dismissed');
  return html`<div
    class="cat-menu argus-ibx-menu"
    role="menu"
    style=${`left:${x}px; top:${y}px`}
    @contextmenu=${(e: Event) => e.preventDefault()}
  >
    ${judging} ${judged ? html`<div class="cat-menu-sep"></div>` : nothing}
    ${entry('▸', 'Open the rule', () => actions.openRule(item))}
    ${actions.filtered
      ? entry('≡', 'Show every rule', () => actions.filter(null))
      : entry('≡', 'Only this rule', () => actions.filter(item))}
  </div>`;
}
